import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject, filter, take, tap } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { BUTTON_CLICK } from 'src/app/components/shared/modal/exit-modal/exit-modal.constants';
import {
  IMAGE_SRC,
  STATUS_OPTIONS,
} from 'src/app/components/shared/shared-constants';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort } from '@angular/material/sort';
import { InputType, SpecialCharacters } from 'src/app/core/enums';
import {
  GetAllRoutes,
  LocationProfile,
  RouteAdmin,
  UserAdmin,
} from 'src/app/core/models/admin';
import { Items } from 'src/app/core/models/common/items.model';
import { TableColumn } from 'src/app/core/models/common/table-column.model';
import { RouteService } from 'src/app/core/services/admin/route/route.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { LocationValues, TotalRouteAdminControls } from '../route-admin.enum';
import { AddRouteIdoComponent } from './add-route-ido/add-route-ido.component';
import {
  ColumnIds,
  ColumnText,
  DEFAULT_ICONS,
  DEFAULT_LOCATIONS,
  DISPLAY_TEXT,
  EXCEL_FILE_NAME,
  ExcelColumns,
} from './route-admin-ido.constants';

@Component({
  selector: 'app-route-admin-ido',
  templateUrl: './route-admin-ido.component.html',
  styleUrls: ['./route-admin-ido.component.scss'],
  standalone: false,
})
export class RouteAdminIdoComponent implements OnChanges {
  @Input() currentLocation!: LocationProfile;

  readonly destroy = inject(DestroyRef);
  private userService = inject(UserService);

  //Display Constants
  readonly CONSTANTS = DISPLAY_TEXT;
  readonly IDS = Object.values(ColumnIds);
  readonly COLUMNTEXT = ColumnText;
  readonly ICONS = DEFAULT_ICONS;
  readonly IMAGES = IMAGE_SRC;
  readonly ImageOptions = STATUS_OPTIONS;
  readonly INPUT_TYPE = InputType;
  readonly CHARS = SpecialCharacters;
  readonly LOCATION_OPTIONS = DEFAULT_LOCATIONS;
  readonly SELECT_ALL_LABEL = STATUS_OPTIONS.ALL_SELECTED;
  readonly LOCATION_VALUES = LocationValues;
  readonly COLUMN_IDS = ColumnIds;

  dataSource = new MatTableDataSource<RouteAdmin>();
  originalTableData!: RouteAdmin[];

  currentUser!: UserAdmin;

  // Column Search Variables
  routeData = this.commonUtils.createTableColumn(ColumnIds.ROUTE);
  lbtData = this.commonUtils.createTableColumn(ColumnIds.LBT);
  optimizeData = this.commonUtils.createTableColumn(ColumnIds.OPTIMIZE);
  locationStartData = this.commonUtils.createTableColumn(ColumnIds.LOCATION);
  addressStartData = this.commonUtils.createTableColumn(ColumnIds.ADDRESS);
  breakData = this.commonUtils.createTableColumn(ColumnIds.BREAK);
  breakTimeData = this.commonUtils.createTableColumn(ColumnIds.BREAK_TIME);
  durationData = this.commonUtils.createTableColumn(ColumnIds.DURATION);
  delWaitTimeData = this.commonUtils.createTableColumn(ColumnIds.DEL_WAIT_TIME);
  puWaitTimeData = this.commonUtils.createTableColumn(ColumnIds.PU_WAIT_TIME);
  vehicleData = this.commonUtils.createTableColumn(ColumnIds.VEHICLE);
  dropdownOpen = [false];

  resetSelectDropdowns = new BehaviorSubject<boolean>(false);

  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private dialog: MatDialog,
    public commonUtils: CommonUtils,
    private routeService: RouteService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.fetchAllRoutes(changes.currentLocation.currentValue.locationCode);
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        filter((user): user is UserAdmin => !!user),
        tap((user) => {
          this.currentUser = user;
        }),
      )
      .subscribe();
  }

  exportExcel(): void {
    const dataToExport: string[][] = [];
    this.dataSource?.filteredData?.forEach((row: RouteAdmin) => {
      dataToExport.push([
        row.route_id,
        row.leavebldg_time,
        row.is_optimizeroute.toString(),
        row.startend_location,
        row.startend_address,
        row.break_exist.toString(),
        row.break_exist ? row.break_start : '',
        row.break_exist ? row.break_end : '',
        row.break_exist ? row.break_duration : '',
        row.waittime_del,
        row.waittime_pickup,
        row.vehicle,
      ]);
    });
    this.commonUtils.exportExcel(ExcelColumns, EXCEL_FILE_NAME, dataToExport);
  }

  clearAllFilters(): void {
    this.commonUtils.clearAllFilters(
      this.dataSource,
      this.originalTableData,
      [this.routeData.formControl, this.lbtData.formControl],
      [this.locationStartData],
    );
    this.resetSelectDropdowns.next(true);
  }

  search() {
    this.commonUtils.searchFilter(
      [this.routeData, this.lbtData],
      this.dataSource,
      [this.optimizeData, this.locationStartData],
    );
  }

  onStatusChange($event: string | boolean | unknown[], field: TableColumn) {
    field.ids[0] = (Array.isArray($event) ? $event[0] : $event) as Items;
    this.search();
  }

  removeRoute(routeAdmin: RouteAdmin) {
    this.commonUtils
      .openWarningModal({
        header: this.CONSTANTS.DELETE_MODAL.HEADER,
        body: this.CONSTANTS.DELETE_MODAL.BODY,
        extraText: routeAdmin.route_id,
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp.button === BUTTON_CLICK.ACCEPT) {
          this.routeService
            .deleteRoute(
              routeAdmin.route_id,
              this.currentUser?.location,
              this.currentUser?.loginId,
            )
            .pipe(take(1))
            .subscribe(() => {
              this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
              this.fetchAllRoutes(this.currentLocation?.locationCode);
            });
        }
      });
  }

  editRoute(routeAdmin: RouteAdmin) {
    this.dialog
      .open(AddRouteIdoComponent, {
        width: '62.5rem',
        disableClose: true,
        data: { data: routeAdmin, isEdit: true },
      })
      .afterClosed()
      .subscribe((resp: RouteAdmin) => {
        if (resp) {
          this.routeService
            .editRoute(this.mapStatusOptionToString(resp))
            .pipe(take(1))
            .subscribe(() => {
              this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
              this.fetchAllRoutes(this.currentUser?.location);
            });
        }
      });
  }

  addRoute() {
    const addDialog = this.dialog.open(AddRouteIdoComponent, {
      width: '62.5rem',
      disableClose: true,
      data: [null, false, this.currentLocation],
    });

    addDialog?.afterClosed().subscribe((resp: RouteAdmin) => {
      if (resp) {
        this.routeService
          .createRoute(
            this.mapStatusOptionToString(resp),
            this.currentLocation?.locationCode,
          )
          .pipe(take(1))
          .subscribe(() => {
            this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
            this.fetchAllRoutes(this.currentUser?.locationGroup);
          });
      }
    });
  }

  get isFilterEnabled() {
    return (
      this.routeData.formControl.value !== '' ||
      this.lbtData.formControl.value !== '' ||
      this.optimizeData.ids.length > 0 ||
      this.locationStartData.ids.length > 0
    );
  }

  private fetchAllRoutes(locationCode: string) {
    this.routeService
      .getAllRoutes(locationCode)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((data: GetAllRoutes) => {
        const routes = data?.response?.routes;
        routes?.sort((a, b) => a?.route_id?.localeCompare(b?.route_id));
        this.dataSource.data = this.mapStringToStatusOption(
          data?.response?.routes,
        );
        this.dataSource.filterPredicate = this.createCustomFilter();
        this.originalTableData = this.mapStringToStatusOption(
          data?.response?.routes,
        );
      });
  }
  createCustomFilter(): (data: RouteAdmin, filter: string) => boolean {
    return (data: RouteAdmin, filter: string) => {
      const filterValues = JSON.parse(filter);
      return Object.keys(filterValues).every((filterKey) => {
        const value = filterValues[filterKey];
        const rowValue = (data as never)[filterKey] as string;
        const searchData = rowValue?.toString().toLowerCase() || '';
        const searchValue = value?.toString().toLowerCase();
        if (value === 'Other') {
          return !searchData.includes('facility');
        } else {
          const isMultiSelect = searchValue.includes(',');
          return isMultiSelect
            ? [...value].find(
                (item: string) => item.toLowerCase() === searchData,
              ) !== undefined
            : searchData.includes(searchValue);
        }
      });
    };
  }
  mapStringToStatusOption(value: RouteAdmin[]) {
    return value?.map((data) => {
      let newOptimizeRoute = data.is_optimizeroute;
      let newBreakExist = data.break_exist;

      switch (data.is_optimizeroute) {
        case '0':
          newOptimizeRoute = false;
          break;
        case '1':
          newOptimizeRoute = true;
          break;
      }

      switch (data.break_exist) {
        case '0':
          newBreakExist = false;
          break;
        case '1':
          newBreakExist = true;
          break;
      }

      return {
        ...data,
        [TotalRouteAdminControls.OPTIMIZE]: newOptimizeRoute,
        [TotalRouteAdminControls.BREAK]: newBreakExist,
      };
    });
  }

  mapStatusOptionToString(value: RouteAdmin) {
    let newOptimizeRoute = value.is_optimizeroute;
    let newBreakExist = value.break_exist;

    switch (value.is_optimizeroute) {
      case false:
        newOptimizeRoute = '0';
        break;
      case true:
        newOptimizeRoute = '1';
        break;
    }

    switch (value.break_exist) {
      case false:
        newBreakExist = '0';
        break;
      case true:
        newBreakExist = '1';
        break;
    }
    value.is_optimizeroute = newOptimizeRoute;
    value.break_exist = newBreakExist;
    return value;
  }
}
